import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import MobileLayout from '../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface Payment {
  id: string;
  description: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  createdAt: string;
  invoiceUrl?: string;
}

const BillingHistory: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get('/payments/history');
      setPayments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-green-400';
      case 'pending':
        return 'text-yellow-400';
      case 'failed':
        return 'text-red-400';
      default:
        return 'text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary';
    }
  };

  const handleDownloadInvoice = (invoiceUrl?: string) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
          <h1 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text">
            {t('billing.history.title', 'Billing History')}
          </h1>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                {t('common.loading', 'Loading...')}
              </div>
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 px-6">
              <div className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center">
                <p className="text-lg mb-2">
                  {t('billing.history.noHistory', 'No payment history')}
                </p>
                <p className="text-sm">
                  {t('billing.history.willAppearHere', 'Your transactions will appear here')}
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-dark-border dark:divide-dark-border divide-light-border">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="p-4 hover:bg-dark-surface/50 dark:hover:bg-dark-surface/50 hover:bg-light-surface/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-dark-text dark:text-dark-text text-light-text font-medium">
                        {payment.description}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                          {formatDate(payment.createdAt)}
                        </span>
                        <span className={`text-sm capitalize ${getStatusColor(payment.status)}`}>
                          {t(`billing.history.status.${payment.status}`, payment.status)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-dark-text dark:text-dark-text text-light-text font-semibold">
                        {formatAmount(payment.amount, payment.currency)}
                      </span>
                      {payment.invoiceUrl && (
                        <button
                          onClick={() => handleDownloadInvoice(payment.invoiceUrl)}
                          className="p-2 hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface rounded-lg transition-colors"
                          title={t('billing.history.downloadInvoice', 'Download Invoice')}
                        >
                          <ArrowDownTrayIcon className="w-4 h-4 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};

export default BillingHistory;
